import TablePagination from "@mui/material/TablePagination";
import { useEffect, useMemo, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import {
  ListContextProvider,
  type ListControllerResult,
  useSidebarState,
} from "react-admin";

import { ListNoResultsWithFilters } from "@/components/ListNoResultsWithFilters";
import { BaseModal } from "@/components/modal";
import { STATISTICS_PER_PAGE_OPTIONS } from "@/constants/filters";
import { DECIMAL_RADIX } from "@/constants/numbers";
import { useTypedListContext } from "@/hooks";

import { type StatisticsItemDto } from "../api/types";
import { type StatisticsItem } from "../types";

import { GROUPS, TOTAL_GROUP } from "./constants";
import { StatisticsDatagrid } from "./StatisticsDatagrid";
import { StatisticsListByZoneId } from "./StatisticsListByZoneId";

interface StatisticsDatagridWithModalProps {
  defaultFilters?: Record<string, unknown>;
}

export const StatisticsDatagridWithModal = ({
  defaultFilters,
}: StatisticsDatagridWithModalProps) => {
  const [open] = useSidebarState();
  const list = useTypedListContext<StatisticsItem, { group?: string }>();
  const { data: listData = [], filterValues } = list;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    STATISTICS_PER_PAGE_OPTIONS[0],
  );

  useEffect(() => {
    setPage(0);
  }, [filterValues, list.sort]);

  const total = listData.length;
  const totalItemIndex = listData.findIndex(
    (item) => item.group === TOTAL_GROUP,
  );
  const totalItem =
    totalItemIndex !== -1 ? listData[totalItemIndex] : undefined;
  const allData = totalItem
    ? listData.filter((_, index) => index !== totalItemIndex)
    : listData;

  useEffect(() => {
    if (page * rowsPerPage >= total && page !== 0) setPage(0);
  }, [page, rowsPerPage, total]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const slicedData = allData.slice(start, start + rowsPerPage);
    return totalItem ? slicedData.concat(totalItem) : slicedData;
  }, [allData, page, rowsPerPage, totalItem]);

  const paginatedIds = useMemo(
    () => paginatedData.map(({ id }) => id),
    [paginatedData],
  );

  const paginatedContext = useMemo(
    () => ({
      ...list,
      data: paginatedData,
      ids: paginatedIds,
      total,
    }),
    [list, paginatedData, paginatedIds, total],
  );

  const [openModal, setOpenModal] = useState(false);
  const [currentZoneId, setCurrentZoneId] = useState<string>();

  const handleOpenModal = (group: StatisticsItemDto["group"]) => {
    setCurrentZoneId(group);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleRowClick = (record: StatisticsItemDto): false => {
    if (filterValues.group === GROUPS.ZONE_ID) {
      handleOpenModal(record.group);
    }
    return false;
  };

  return (
    <Fragment>
      <ListContextProvider
        value={paginatedContext as ListControllerResult<StatisticsItem>}
      >
        <StatisticsDatagrid
          className={`datagrid-scroll ${!open ? "collapsed" : ""}`}
          empty={<ListNoResultsWithFilters defaultFilters={defaultFilters} />}
          rowClick={(_id, _resource, record) =>
            handleRowClick(record as StatisticsItemDto)
          }
        />
      </ListContextProvider>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, DECIMAL_RADIX));
          setPage(0);
        }}
        rowsPerPageOptions={STATISTICS_PER_PAGE_OPTIONS}
      />

      <BaseModal
        title={`Statistics Details for Zone ID: ${currentZoneId}`}
        onClose={handleCloseModal}
        open={openModal}
        sx={{
          width: "100%",
          maxWidth: "min(90vw, 1600px)",
          minHeight: "300px",
        }}
      >
        <StatisticsListByZoneId zoneId={currentZoneId} filters={filterValues} />
      </BaseModal>
    </Fragment>
  );
};
